import React, { useState, useMemo } from 'react';
import { Box, Typography, useTheme, Grid } from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { 
  getColorByLabel, 
  getHighlightedColorByLabel, 
  generateColorArrayByLabels, 
} from '../utils/colorMapping';

const EnhancedChartGroup = ({ 
  title, 
  barData, 
  multiBarData,
  chartData,
  xAxisTitle = 'Time Period',
  yAxisTitle = 'Count',
  isExpanded = false,
  visibleDatasets = null,
  sectionType = null,
  tripTypeOptions = []
}) => {
  const theme = useTheme();
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  
  // Use passed visible datasets or default to all datasets
  const datasetsToShow = visibleDatasets || (multiBarData && multiBarData.datasets ? multiBarData.datasets : []);
  
  // Get time period labels directly from the data
  const timeLabels = useMemo(() => {
    // Priority 1: Use server-provided timeLabels from multiBarData
    if (multiBarData && multiBarData.timeLabels && multiBarData.timeLabels.length > 0) {
      return multiBarData.timeLabels;
    }
    
    // Fallback: return empty array if no time labels available
    return [];
  }, [barData, multiBarData]);

  // Get readable trip type label
  const getTripTypeLabel = useMemo(() => {
    // First check if it's available in chartData.appliedFilters
    if (chartData?.appliedFilters?.tripType && tripTypeOptions.length > 0) {
      const option = tripTypeOptions.find(opt => opt.value === chartData.appliedFilters.tripType);
      return option ? option.label : chartData.appliedFilters.tripType;
    }
    
    // Fall back to the tripType prop

    return null;
  }, [sectionType,tripTypeOptions, chartData]);

  // First pie chart - sums of each time period across visible/toggled datasets only
  const timePeriodSums = useMemo(() => {
    if (!multiBarData || !multiBarData.datasets) return [];
    
    // Use visible datasets if available, otherwise fall back to all datasets
    const datasetsToUse = datasetsToShow.length > 0 ? datasetsToShow : multiBarData.datasets;
    
    // For each time period, sum values across only the visible/toggled datasets
    const periodCount = multiBarData.timestamps ? multiBarData.timestamps.length : (timeLabels ? timeLabels.length : 0);
    return Array(periodCount).fill(0).map((_, periodIndex) => {
      return datasetsToUse.reduce((sum, dataset) => {
        return sum + (dataset && dataset.values ? (dataset.values[periodIndex] || 0) : 0);
      }, 0);
    });
  }, [multiBarData, timeLabels, datasetsToShow]);
  
  // Early return if no data is available
  if (!barData && !multiBarData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: 200,
        backgroundColor: '#f5f5f5',
        borderRadius: 2,
        border: `1px dashed ${theme.palette.grey[300]}`
      }}>
        <Typography variant="body1" color="text.secondary">
          No chart data available
        </Typography>
      </Box>
    );
  }

  // Early return if we have data but no time labels (which can happen with "specific" filter and no selection)
  if (multiBarData && (!timeLabels || timeLabels.length === 0)) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: 200,
        backgroundColor: '#f5f5f5',
        borderRadius: 2,
        border: `1px dashed ${theme.palette.grey[300]}`
      }}>
        <Typography variant="body1" color="text.secondary">
          No time period data available. Please ensure filters are properly configured.
        </Typography>
      </Box>
    );
  }
  
  // Generate darker border color for highlighting
  const getDarkerBorderColor = (label, chartType = 'bar') => {
    const baseColor = getColorByLabel(label, 'border', chartType);
    // Extract RGB values and make them darker
    return baseColor.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),/, (match, r, g, b) => {
      const darkR = Math.max(0, Math.floor(parseInt(r) * 0.7));
      const darkG = Math.max(0, Math.floor(parseInt(g) * 0.7));
      const darkB = Math.max(0, Math.floor(parseInt(b) * 0.7));
      return `rgba(${darkR}, ${darkG}, ${darkB},`;
    });
  };
  
  // Bar chart data with label-based color mapping
  const barChartData = {
    labels: timeLabels,
    datasets: multiBarData && multiBarData.datasets ? 
      (datasetsToShow.length > 0 ? datasetsToShow : multiBarData.datasets)
        .filter(dataset => dataset && dataset.values) // Filter out invalid datasets
        .map((dataset, datasetIndex) => {
        // Find the original dataset index for color consistency
        const originalDatasetIndex = multiBarData.datasets.findIndex(d => d && d.label === dataset.label);
        
        return {
          label: dataset.label || 'Unknown Dataset',
          data: dataset.values || [],
          backgroundColor: (() => {
            if (highlightedIndex !== null && dataset.values) {
              // First pie chart hover: highlight all datasets at the specific period
              return dataset.values.map((_, periodIndex) => {
                if (periodIndex === highlightedIndex) {
                  return getHighlightedColorByLabel(dataset.label, 'bg', 'bar');
                }
                return getColorByLabel(dataset.label, 'bg', 'bar');
              });
            }
            return getColorByLabel(dataset.label, 'bg', 'bar');
          })(),
          borderColor: (() => {
            if (highlightedIndex !== null && dataset.values) {
              // First pie chart hover: border for all datasets at the specific period
              return dataset.values.map((_, periodIndex) => {
                if (periodIndex === highlightedIndex) {
                  return getDarkerBorderColor(dataset.label, 'bar');
                }
                return 'transparent';
              });
            }
            return 'transparent';
          })(),
          borderWidth: (() => {
            if (highlightedIndex !== null && dataset.values) {
              // First pie chart hover: border width for all datasets at the specific period
              return dataset.values.map((_, periodIndex) => {
                if (periodIndex === highlightedIndex) {
                  return 3;
                }
                return 0;
              });
            }
            return 0;
          })()
        };
      }) : 
      (barData && barData.values ? [{
        label: title || 'Chart Data',
        data: barData.values,
        backgroundColor: highlightedIndex !== null ? 
          generateColorArrayByLabels(timeLabels, 'bg', 'bar', highlightedIndex) : 
          generateColorArrayByLabels(timeLabels, 'bg', 'bar'),
        borderColor: highlightedIndex !== null ? 
          (timeLabels && timeLabels.length > 0 ? timeLabels.map((label, index) => 
            index === highlightedIndex ? getDarkerBorderColor(label, 'bar') : 'transparent'
          ) : []) : 
          'transparent',
        borderWidth: highlightedIndex !== null ? 3 : 0
      }] : [])
  };
  
  const timePeriodPieData = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Time Period Totals',
        data: timePeriodSums,
        backgroundColor: generateColorArrayByLabels(timeLabels, 'bg', 'pie', highlightedIndex),
        borderColor: 'transparent',
        borderWidth: 0
      }
    ]
  };

  // Chart options with sync highlighting
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      bar: {
        borderWidth: 0,
        borderSkipped: false
      }
    },
    onHover: (event, elements) => {
      // Bar chart hover should NOT trigger highlighting borders
      // Only pie chart hovers should trigger the highlighting state
      // The Chart.js built-in hover effects will handle bar appearance changes
    },
    plugins: {
      legend: {
        display: multiBarData ? true : false,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
        }
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItems) => {
            if (!timeLabels || timeLabels.length === 0 || !tooltipItems || tooltipItems.length === 0) {
              return 'Data Point';
            }
            const fullLabel = timeLabels[tooltipItems[0].dataIndex];
            return `Time Period: ${fullLabel}`;
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            
            // Calculate percentage of total for this time period
            let percentageText = '';
            if (multiBarData) {
              const periodIndex = context.dataIndex;
              let periodTotal = 0;
              
              // Sum all values for this time period across all datasets
              multiBarData.datasets.forEach(dataset => {
                periodTotal += (dataset && dataset.values ? (dataset.values[periodIndex] || 0) : 0);
              });
              
              if (periodTotal > 0) {
                const percentage = Math.round((value / periodTotal) * 100);
                percentageText = `${percentage}% of period total`;
              }
            }
            
            // Return the main value without percentage
            return `${label}: ${value}`;
          },
          
          // Add a new callback to display percentage on a new row
          afterLabel: (context) => {
            // First display percentage if available
            let lines = [];
            
            // Calculate percentage text like before
            if (multiBarData) {
              const periodIndex = context.dataIndex;
              let periodTotal = 0;
              
              // Sum all values for this time period across all datasets
              multiBarData.datasets.forEach(dataset => {
                periodTotal += (dataset && dataset.values ? (dataset.values[periodIndex] || 0) : 0);
              });
              
              if (periodTotal > 0) {
                const value = context.parsed.y;
                const percentage = Math.round((value / periodTotal) * 100);
                lines.push(`${percentage}% of period total`);
              }
            }
            
            // Then add the period total
            if (multiBarData) {
              const periodIndex = context.dataIndex;
              let periodTotal = 0;
              
              // Sum all values for this time period across all datasets
              multiBarData.datasets.forEach(dataset => {
                periodTotal += (dataset && dataset.values ? (dataset.values[periodIndex] || 0) : 0);
              });
              
              lines.push(`Period Total: ${periodTotal}`);
            }
            
            return lines;
          },
          afterLabel: (context) => {
            // First display percentage if available
            let lines = [];
            
            // Calculate percentage text like before
            if (multiBarData) {
              const periodIndex = context.dataIndex;
              let periodTotal = 0;
              
              // Sum all values for this time period across all datasets
              multiBarData.datasets.forEach(dataset => {
                periodTotal += (dataset && dataset.values ? (dataset.values[periodIndex] || 0) : 0);
              });
              
              if (periodTotal > 0) {
                const value = context.parsed.y;
                const percentage = Math.round((value / periodTotal) * 100);
                lines.push(`${percentage}% of period total`);
              }
            }
            
            // Then add the period total
            if (multiBarData) {
              const periodIndex = context.dataIndex;
              let periodTotal = 0;
              
              // Sum all values for this time period across all datasets
              multiBarData.datasets.forEach(dataset => {
                periodTotal += (dataset && dataset.values ? (dataset.values[periodIndex] || 0) : 0);
              });
              
              lines.push(`Period Total: ${periodTotal}`);
            }
            
            return lines;
          }
        }
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        type: 'category',
        title: {
          display: true,
          text: xAxisTitle,

        },
        ticks: {
          maxRotation: 45,
          minRotation: 15,
          callback: function(value, index) {
            // For very long labels, truncate and add ellipsis if needed
            const label = this.getLabelForValue(value);
            if (label && label.length > 20) {
              return label.substring(0, 17) + '...';
            }
            return label;
          }
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: yAxisTitle,
        }
      }
    },
    onClick: (event, elements) => {
      // Bar chart clicks should NOT trigger highlighting
      // Only pie chart interactions should control highlighting
    }
  };
  
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event, elements) => {
      if (elements && elements.length > 0) {
        setHighlightedIndex(elements[0].index);
      } else {
        setHighlightedIndex(null);
      }
    },
    plugins: {
      legend: {
        display: false,
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,

        }
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItems) => {
            if (!timeLabels || timeLabels.length === 0 || !tooltipItems || tooltipItems.length === 0) {
              return 'Data Point';
            }
            return timeLabels[tooltipItems[0].dataIndex];
          },
          label: (context) => {
            const label = context.label || '';
            const value = context.raw;
            const total = context.chart.getDatasetMeta(0).total;
            const percentage = Math.round((value / total) * 100);
            
            return `${label}: ${value} (${percentage}%)`;
          },
          afterLabel: (context) => {
            // Add time period info
            if (!timeLabels || timeLabels.length === 0) {
              return '';
            }
            return `Time: ${timeLabels[context.dataIndex]}`;
          }
        }
      },
      title: {
        display: false
      }
    },
    onClick: (event, elements) => {
      if (elements && elements.length > 0) {
        setHighlightedIndex(elements[0].index);
      } else {
        setHighlightedIndex(null);
      }
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%', 
        mb: 2,
        p: 2,
        borderRadius: 3,
        background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        border: `1px solid ${theme.palette.grey[100]}`
      }}
      onMouseLeave={() => {
        // Clear all highlighting when mouse leaves the entire chart group
        setHighlightedIndex(null);
      }}
    >
      <Typography 
        variant="h5" 
        gutterBottom 
        component="div" 
        sx={{ 
          textAlign: 'center',
          fontWeight: 700,
          color: theme.palette.primary.main,
          mb: 3,
          fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
          letterSpacing: '0.5px',
          display: title ? 'block' : 'none' // Only show if title exists
        }}
      >
        {title}
      </Typography>
      
      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Bar Chart - 70% width on desktop for expanded, 60% for minimized, full width on mobile */}
        <Grid item xs={12} lg={isExpanded ? 8.4 : 7.2}>
          <Box sx={{ 
            height: isExpanded 
              ? { xs: 350, sm: 400, md: 450, lg: 500 }
              : { xs: 300, sm: 350, md: 400, lg: 420 }, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
            p: 3, 
            borderRadius: 3,
            mb: { xs: 3, lg: 0 },
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            border: `1px solid ${theme.palette.grey[100]}`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              borderRadius: '3px 3px 0 0'
            }
          }}>
            <Typography 
              variant="subtitle1" 
              align="center" 
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.primary.main,
                mb: getTripTypeLabel ? 1 : 2,
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
              }}
            >
              {multiBarData ? 'Multi-Series Analysis' : 'Time Series Analysis'}
            </Typography>
            
            {/* Display selected trip path type for trips section */}
            {getTripTypeLabel && (
              <Typography 
                variant="body2" 
                align="center" 
                sx={{ 
                  fontWeight: 500, 
                  color: theme.palette.text.secondary,
                  mb: 2,
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                  fontStyle: 'italic'
                }}
              >
                Path Type: {getTripTypeLabel}
              </Typography>
            )}
            <Box sx={{ height: 'calc(100% - 40px)' }}>
              <Bar data={barChartData} options={barOptions} />
            </Box>
          </Box>
        </Grid>
        
        {/* Pie Chart Container - 30% width on desktop for expanded, 40% for minimized */}
        <Grid item xs={12} lg={isExpanded ? 3.6 : 4.8}>
          <Box sx={{ 
            p: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
            borderRadius: 3, 
            display: 'flex',
            flexDirection: 'column',
            height: isExpanded 
              ? { xs: 350, sm: 400, md: 450, lg: 500 }
              : { xs: 300, sm: 350, md: 400, lg: 420 },
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            border: `1px solid ${theme.palette.grey[100]}`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              borderRadius: '3px 3px 0 0'
            }
          }}>
            <Typography 
              variant="subtitle1" 
              align="center" 
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.primary.main,
                mb: 2,
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                flexShrink: 0
              }}
            >
              Time Period Distribution
            </Typography>
            <Box sx={{ 
              height: 'calc(100% - 40px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              overflow: 'hidden'
            }}>
              <Pie data={timePeriodPieData} options={pieOptions} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnhancedChartGroup;
